import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';

// ---------------------------------------------------------------------------
// Overlay — lightweight fullscreen overlay primitive
// ---------------------------------------------------------------------------

/**
 * Reimagined Overlay — a minimal fullscreen overlay with backdrop and portal.
 *
 * Unlike Modal, this provides NO panel chrome (no close button, no size
 * constraints, no named blocks). It simply portals a frosted-glass backdrop
 * behind arbitrary yielded content and handles escape-to-close + body scroll
 * lock.
 *
 * Use this when you need a raw overlay surface and want full control over
 * the inner layout (e.g. search palettes, command bars, custom drawers).
 *
 * @example
 * ```gts
 * import Overlay from 'tio-ui/components/overlay';
 *
 * <Overlay @isOpen={{this.showPalette}} @onClose={{this.closePalette}} class="z-[90]">
 *   <div class="mx-auto max-w-xl mt-20 tio-bg-surface rounded-xl shadow-2xl">
 *     ...custom content...
 *   </div>
 * </Overlay>
 * ```
 */

interface OverlaySignature {
  Args: {
    /** Whether the overlay is visible */
    isOpen: boolean;
    /** Called when the overlay requests to close (Escape key or backdrop click) */
    onClose: () => void;
    /** Close when clicking the backdrop @default true */
    closeOnBackdropClick?: boolean;
    /** Close when pressing Escape @default true */
    closeOnEscapeKey?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class Overlay extends Component<OverlaySignature> {
  @tracked private _mounted = false;
  @tracked private _visible = false;

  private overlayId = crypto.randomUUID();

  /** Persisted across modifier re-runs so teardown always has the correct value */
  private savedScrollY = 0;

  get portalDestination(): Element {
    // In test environments Ember renders into #ember-testing.  Default
    // qunit-dom assertions scope to that container, so we portal there
    // too — mirroring @frontile/overlays' Portal behaviour.
    return document.getElementById('ember-testing') ?? document.body;
  }

  private get closeOnBackdropClick() {
    return this.args.closeOnBackdropClick ?? true;
  }

  private get closeOnEscapeKey() {
    return this.args.closeOnEscapeKey ?? true;
  }

  // --------------------------------------------------
  // Lifecycle modifier — mounts/unmounts with animation
  // --------------------------------------------------

  manageLifecycle = modifier((_element: HTMLElement, [isOpen]: [boolean]) => {
    let rafId: number | undefined;

    if (isOpen) {
      this._mounted = true;

      // Lock body scroll
      this.savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      // Trigger fade-in on next frame
      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        this._visible = true;

        const overlay = document.querySelector<HTMLElement>(
          `[data-overlay-id="${this.overlayId}"]`
        );
        if (overlay) {
          // Prefer an explicitly marked focus target inside the overlay
          // (e.g. a search input via [data-autofocus]) so opening the overlay
          // doesn't blur it by focusing the container instead.
          const focusTarget = overlay.querySelector<HTMLElement>('[data-autofocus]');
          (focusTarget ?? overlay).focus({ preventScroll: true });
        }
      });
    } else if (this._mounted) {
      this._visible = false;

      const cleanup = setTimeout(() => {
        this._mounted = false;
      }, 300);

      return () => clearTimeout(cleanup);
    }

    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      // Restore body scroll
      if (document.body.style.position === 'fixed') {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, this.savedScrollY);
      }
    };
  });

  // --------------------------------------------------
  // Event handlers
  // --------------------------------------------------

  handleBackdropClick = (event: MouseEvent) => {
    // Only close if the click target IS the overlay container (backdrop area),
    // not content inside it
    if (this.closeOnBackdropClick && event.target === event.currentTarget) {
      this.args.onClose();
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (this.closeOnEscapeKey && event.key === 'Escape') {
      event.stopPropagation();
      this.args.onClose();
    }
  };

  <template>
    {{! Anchor element for the lifecycle modifier }}
    <div
      class="reimagined-overlay-anchor sr-only"
      {{this.manageLifecycle @isOpen}}
      aria-hidden="true"
    ></div>

    {{#if this._mounted}}
      {{#in-element this.portalDestination insertBefore=null}}
        <div
          data-test-overlay
          data-overlay-id={{this.overlayId}}
          class="fixed inset-0 z-50 overflow-y-auto transition-opacity duration-300
            {{if this._visible 'opacity-100' 'opacity-0'}}"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          {{on "keydown" this.handleKeyDown}}
          {{on "click" this.handleBackdropClick}}
          ...attributes
        >
          {{! Frosted glass backdrop }}
          <div
            data-test-overlay-backdrop
            class="tio-overlay__backdrop fixed inset-0"
            aria-hidden="true"
          ></div>

          {{! Content layer (above backdrop) }}
          <div class="relative">
            {{yield}}
          </div>
        </div>
      {{/in-element}}
    {{/if}}
  </template>
}

export type { OverlaySignature };
